import { useMemo, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { AlertTriangle, Check, ChevronRight, Undo2 } from 'lucide-react';
import { cn } from '@/lib/cn';
import { formatAgo, formatDateTime } from '@/lib/format';
import { useAnomalies, useUnackAnomaly } from '@/api/hooks/anomalies';
import type { Anomaly, AnomalyCategory } from '@/api/types';
import { AckDialog, SEVERITY, anomalyLink } from '@/components/anomalies/AnomalyParts';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Checkbox } from '@/components/ui/Field';
import { EmptyState, ErrorState, LoadingBlock } from '@/components/ui/State';

const ORDER: AnomalyCategory[] = ['km', 'documents', 'trips', 'deadlines', 'service', 'fuel', 'money'];

/** Колко реда от раздел се виждат, преди „Покажи всички". */
const PREVIEW = 8;

/**
 * ═══════════════════════════════════════════════════════════════════════════
 *  АНОМАЛИИ — „нещо не е наред" от всички модули
 *
 *  Километраж срещу GPS, гориво извън нормата, заявки без документи,
 *  закъснели доставки, бусове без проверка, просрочени фактури, разходи без
 *  бележка. Всеки ред води там, където се оправя; „Прегледано" го маха от
 *  списъка (с бележка защо), а оправеното изчезва само.
 * ═══════════════════════════════════════════════════════════════════════════
 */
export function AnomaliesPage() {
  const [showAcked, setShowAcked] = useState(false);
  const [onlyHigh, setOnlyHigh] = useState(false);
  const [category, setCategory] = useState<AnomalyCategory | 'all'>('all');
  const [expanded, setExpanded] = useState<Set<AnomalyCategory>>(new Set());
  const [acking, setAcking] = useState<Anomaly | null>(null);
  const { data, isLoading, isError, refetch } = useAnomalies(showAcked);
  const unack = useUnackAnomaly();

  const groups = useMemo(() => {
    const items = (data?.items ?? []).filter(
      (a) => (category === 'all' || a.category === category) && (!onlyHigh || a.severity === 'high'),
    );
    return ORDER.map((c) => ({ category: c, items: items.filter((a) => a.category === c) })).filter((g) => g.items.length > 0);
  }, [data, category, onlyHigh]);

  if (isLoading) return <LoadingBlock />;
  if (isError || !data) return <ErrorState onRetry={() => void refetch()} />;

  return (
    <div className="space-y-3 p-3 sm:p-4">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="flex items-center gap-2 font-display text-lg font-bold tracking-tight">
          <AlertTriangle className="size-5 text-ink-dim" />
          Аномалии
        </h1>
        <span className="text-xs text-ink-dim">
          {data.counts.open} за преглед · <span className="text-danger">{data.counts.high} важни</span>
        </span>
        <span className="ml-auto flex flex-wrap items-center gap-3">
          <Checkbox label="Само важните" checked={onlyHigh} onChange={(e) => setOnlyHigh(e.target.checked)} />
          <Checkbox label="И прегледаните" checked={showAcked} onChange={(e) => setShowAcked(e.target.checked)} />
        </span>
      </div>

      {/* Разделите като филтър — колко има във всеки се вижда, преди да се отвори. */}
      <div className="flex flex-wrap gap-1.5">
        <CategoryChip active={category === 'all'} onClick={() => setCategory('all')} label="Всички" count={data.counts.open} />
        {ORDER.map((c) => (
          <CategoryChip
            key={c}
            active={category === c}
            onClick={() => setCategory(c)}
            label={data.categories[c]}
            count={data.counts.byCategory[c] ?? 0}
          />
        ))}
      </div>

      {groups.length === 0 ? (
        <Card>
          <EmptyState title="Няма аномалии." body="Всичко, което системата проверява, изглежда наред." />
        </Card>
      ) : (
        groups.map(({ category: c, items }) => {
          const open = expanded.has(c) || items.length <= PREVIEW;
          return (
            <Card key={c} className="overflow-hidden">
              <div className="flex items-center gap-2 border-b border-line px-3 py-2">
                <span className="text-sm font-medium">{data.categories[c]}</span>
                <span className="text-xs text-ink-faint">{items.length}</span>
              </div>
              <ul className="divide-y divide-line">
                {(open ? items : items.slice(0, PREVIEW)).map((a) => (
                  <li key={a.key} className={cn('flex items-start gap-3 px-3 py-2.5', a.acked && 'opacity-60')}>
                    <span className={cn('mt-1.5 size-2 shrink-0 rounded-full', SEVERITY[a.severity].dot)} title={SEVERITY[a.severity].label} />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm">{a.title}</div>
                      {a.detail ? <div className="text-xs text-ink-dim">{a.detail}</div> : null}
                      <div className="mt-0.5 text-[11px] text-ink-faint" title={formatDateTime(a.at)}>
                        {formatAgo(a.at)}
                        {a.acked
                          ? ` · прегледано${a.acked.byName ? ` от ${a.acked.byName}` : ''}${a.acked.note ? `: ${a.acked.note}` : ''}`
                          : ''}
                      </div>
                    </div>
                    <div className="flex shrink-0 items-center gap-1">
                      {a.link ? (
                        <Link
                          {...anomalyLink(a.link)}
                          className="inline-flex items-center gap-0.5 rounded px-2 py-1 text-xs text-accent hover:bg-raised"
                        >
                          Отвори <ChevronRight className="size-3.5" />
                        </Link>
                      ) : null}
                      {a.acked ? (
                        <Button size="sm" variant="ghost" icon={<Undo2 className="size-3.5" />} onClick={() => unack.mutate(a.key)}>
                          Върни
                        </Button>
                      ) : (
                        <Button size="sm" variant="secondary" icon={<Check className="size-3.5" />} onClick={() => setAcking(a)}>
                          Прегледано
                        </Button>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
              {!open ? (
                <button
                  type="button"
                  onClick={() => setExpanded(new Set([...expanded, c]))}
                  className="w-full border-t border-line px-3 py-2 text-left text-xs text-accent hover:bg-raised"
                >
                  Покажи всички {items.length}
                </button>
              ) : null}
            </Card>
          );
        })
      )}

      <AckDialog anomaly={acking} onClose={() => setAcking(null)} />
    </div>
  );
}

function CategoryChip({ active, onClick, label, count }: { active: boolean; onClick: () => void; label: string; count: number }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs',
        active ? 'border-accent bg-accent-soft text-accent' : 'border-line-strong text-ink-dim hover:text-ink',
      )}
    >
      {label}
      <span className={cn('rounded-full px-1.5 text-[10px] font-semibold', count > 0 ? 'bg-raised text-ink' : 'text-ink-faint')}>{count}</span>
    </button>
  );
}
